import { useQuery } from '@tanstack/react-query';

import { queryKeys } from '~/api/queryKeys';

import { fetchContentAttributes, fetchKpis } from '../services/config.service';

/**
 * KPI catalogue for the chosen ad platform (e.g. `google`, `meta`). Disabled
 * until a platform is known. Cached aggressively — KPI lists rarely change
 * within a session.
 */
export function useKpis(platform: string | null) {
  return useQuery({
    queryKey: queryKeys.config.kpis(platform ?? ''),
    queryFn: () => {
      if (!platform) throw new Error('platform is required');
      return fetchKpis(platform);
    },
    enabled: !!platform,
    staleTime: 10 * 60 * 1000,
  });
}

/**
 * Content-attribute taxonomy — a static product-level config that's identical
 * every call and never changes within (or across) a session. Cached permanently:
 * `staleTime: Infinity` never auto-refetches (mount/focus/reconnect all skip it)
 * and `gcTime: Infinity` keeps the one shared cache entry alive, so it's fetched
 * at most once per session. `enabled` lets callers defer the fetch (e.g. the
 * resume prereqs skip it during a fresh-create run, where it isn't needed until
 * Step 4).
 */
export function useContentAttributes(enabled = true) {
  return useQuery({
    queryKey: queryKeys.config.contentAttributes(),
    queryFn: fetchContentAttributes,
    enabled,
    staleTime: Infinity,
    gcTime: Infinity,
  });
}
